/**
 * Tracks whether this browser has completed a sign-in before.
 *
 * Microsoft Entra caps the SPA refresh-token lifetime at ~24h, so a returning
 * user's silent renewal eventually fails and the app falls back to the entry
 * route. For someone who has signed in before we want that fallback to be a
 * seamless redirect back to the IdP (a silent bounce while the Entra session
 * cookie is alive), not a manual "Sign in" click. First-time visitors, by
 * contrast, must still see the landing page (and its waitlist CTA), so we only
 * auto-redirect when this flag is set.
 *
 * The flag is set on the first successful authentication and cleared on an
 * explicit sign-out (so signing out returns the user to the landing page rather
 * than trapping them in an immediate redirect back to the IdP).
 *
 * localStorage access can throw (storage disabled/blocked, e.g. privacy mode or
 * a partitioned context). Guard every access: on failure we degrade to
 * "not a returning user", which just shows the landing page — never a crash.
 */

const RETURNING_USER_KEY = "tower.hasSignedIn";

/**
 * Entry route for an account whose Tower access was revoked (signed in to Entra,
 * but no firm membership). The entry route shows a no-access notice and never
 * auto-redirects from here.
 */
export const ACCESS_DENIED_ENTRY = "/?access=denied";

/** Whether the current entry URL is the revoked-access landing. */
export function isAccessDeniedEntry(search: string): boolean {
  return new URLSearchParams(search).get("access") === "denied";
}

export function markReturningUser(): void {
  try {
    globalThis.window?.localStorage.setItem(RETURNING_USER_KEY, "1");
  } catch {
    // Storage unavailable — auto-redirect simply won't trigger next time.
  }
}

export function clearReturningUser(): void {
  try {
    globalThis.window?.localStorage.removeItem(RETURNING_USER_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

export function isReturningUser(): boolean {
  try {
    return globalThis.window?.localStorage.getItem(RETURNING_USER_KEY) === "1";
  } catch {
    return false;
  }
}
